/**
 * Columns — a split where each column is its own drop zone.
 *
 * This exists because Grid alone made the common case awkward. A Grid cell holds one block,
 * so "image, then heading, then body, in the left column" meant Grid → Stack → three blocks,
 * twice. Three levels of nesting for something that should be obvious.
 *
 * Here each column is a slot, so you drop as many elements into it as you like and they
 * stack. Section → Heading → Columns is then the whole of the usual page structure.
 *
 * This is Elementor's Section/Column idea, deliberately kept as *one* block rather than two
 * levels — the columns are properties of the split, not separate things to select and manage.
 * Each column still gets its own settings group, so a column can be aligned, padded and sized
 * independently without becoming a selectable element in its own right.
 */

import {
  el,
  slotContainer,
  type BlockSignal,
  type BlockSpec,
  type ClassRef,
  type FieldSpec,
  type IRNode,
  type Issue,
} from '@lp/shared';
import { boxCls, SPACE_OPTIONS } from '../shared/util.js';

/** Anything can go in a column, including another split. */
const ANY = [
  'Stack',
  'Grid',
  'Columns',
  'Heading',
  'TextBlock',
  'ButtonBlock',
  'ImageBlock',
  'IconBlock',
  'ListBlock',
  'LinkBlock',
  'DividerBlock',
  'EmbedBlock',
  'FeatureCard',
];

export const MAX_COLUMNS = 4;

/**
 * Track layouts, keyed by the split.
 *
 * The asymmetric options matter more than they look: an even 50/50 gives body copy more
 * measure than reads comfortably next to an image, which is why most real feature rows on
 * the live site are closer to 2:1.
 */
export const SPLIT_OPTIONS = [
  { label: 'Two — equal', value: '1-1' },
  { label: 'Two — wide left', value: '2-1' },
  { label: 'Two — wide right', value: '1-2' },
  { label: 'Three — equal', value: '1-1-1' },
  { label: 'Four — equal', value: '1-1-1-1' },
] as const;

/** How many columns a split describes. */
export function columnCount(split: string): number {
  return Math.min(split.split('-').length, MAX_COLUMNS);
}

/* ---------------------------------------------------------------- *
 * Vertical alignment
 * ---------------------------------------------------------------- */

export type VAlign = 'start' | 'center' | 'end';

/**
 * Vertical placement is `justify-content` on the column, never `align-items` on the grid.
 *
 * `align-items: center` on the grid was the obvious implementation and it silently did
 * nothing in the editor: Puck's own `.DropZone` rule sets `height: 100%` on every slot, so
 * each column already fills its row and there is no free space left to centre it in. The
 * export had no such rule, so the same page centred in Webflow and sat top-aligned here.
 *
 * Making the grid *always* stretch and moving the choice to `justify-content` inside each
 * column removes the disagreement: the column box is full-height in both consumers, and the
 * content is placed within it by a rule that behaves identically in both.
 */
const JUSTIFY: Record<VAlign, string> = {
  start: 'is-justify-start',
  center: 'is-justify-center',
  end: 'is-justify-end',
};

/** Tolerates the older `stretch` value, which predates per-column alignment. */
export function readVAlign(value: unknown, fallback: VAlign = 'start'): VAlign {
  return value === 'start' || value === 'center' || value === 'end' ? value : fallback;
}

const VALIGN_OPTIONS = [
  { label: 'Top', value: 'start' },
  { label: 'Middle', value: 'center' },
  { label: 'Bottom', value: 'end' },
] as const;

/* ---------------------------------------------------------------- *
 * Per-column settings
 * ---------------------------------------------------------------- */

/**
 * What one column can override.
 *
 * Empty string means "take the row's setting" throughout, so a column only carries the
 * choices someone actually made. That keeps the common case — every column the same — a
 * single edit at the top of the panel rather than four identical edits.
 */
export interface ColumnOptions extends Record<string, unknown> {
  /** Vertical placement of this column's content. `''` inherits the row. */
  valign: '' | VAlign;
  /** Horizontal placement. `''` stretches children to the full column width. */
  align: '' | 'start' | 'center' | 'end';
  /** Padding inside the column, as a spacing-scale step. */
  pad: string;
}

export const COLUMN_DEFAULTS: ColumnOptions = { valign: '', align: '', pad: '0' };

/** Tolerates a column that predates these settings, or a partially-filled object. */
export function readColumnOptions(value: unknown): ColumnOptions {
  if (!value || typeof value !== 'object') return { ...COLUMN_DEFAULTS };
  const v = value as Partial<ColumnOptions>;
  return {
    valign: v.valign === 'start' || v.valign === 'center' || v.valign === 'end' ? v.valign : '',
    align: v.align === 'start' || v.align === 'center' || v.align === 'end' ? v.align : '',
    pad: typeof v.pad === 'string' ? v.pad : '0',
  };
}

const columnOptionsField = (n: number): FieldSpec => ({
  type: 'object',
  label: `Column ${n} settings`,
  fields: {
    valign: {
      type: 'select',
      label: 'Vertical',
      help: 'Where this column’s content sits against a taller neighbour.',
      options: [{ label: 'Same as row', value: '' }, ...VALIGN_OPTIONS],
    },
    align: {
      type: 'select',
      label: 'Horizontal',
      options: [
        { label: 'Full width', value: '' },
        { label: 'Left', value: 'start' },
        { label: 'Centre', value: 'center' },
        { label: 'Right', value: 'end' },
      ],
    },
    pad: { type: 'select', label: 'Padding', options: [...SPACE_OPTIONS] },
  },
});

export interface ColumnsProps extends Record<string, unknown> {
  split: string;
  gap: string;
  valign: VAlign;
  reverseOnMobile: boolean;
  col1: unknown;
  col2: unknown;
  col3: unknown;
  col4: unknown;
  col1Opts: ColumnOptions;
  col2Opts: ColumnOptions;
  col3Opts: ColumnOptions;
  col4Opts: ColumnOptions;
}

/** Classes for one column, given the row default and that column's overrides. */
function columnSlot(opts: ColumnOptions, rowValign: VAlign): ClassRef[] {
  const valign = opts.valign || rowValign;
  return boxCls(
    'is-flex',
    'is-col',
    // Without this the cell is only as tall as its content, and JUSTIFY has no space to work
    // in. See `is-cell`.
    'is-cell',
    'is-gap-5',
    JUSTIFY[valign],
    opts.align && `is-align-${opts.align}`,
    opts.pad !== '0' && `is-pad-${opts.pad}`,
  ).map((c) => (typeof c === 'string' ? { name: c } : c)) as ClassRef[];
}

export const Columns: BlockSpec<ColumnsProps> = {
  id: 'Columns',
  label: 'Columns',
  category: 'both',
  description: 'Split into columns. Each one holds as many elements as you like.',

  fields: {
    split: { type: 'radio', label: 'Split', options: [...SPLIT_OPTIONS] },
    gap: { type: 'select', label: 'Gap between columns', options: [...SPACE_OPTIONS] },
    valign: {
      type: 'select',
      label: 'Vertical alignment',
      help: 'Applies to every column. Override one below.',
      options: [...VALIGN_OPTIONS],
    },
    reverseOnMobile: {
      type: 'boolean',
      label: 'Reverse on mobile',
      help: 'Columns stack top-to-bottom on a phone. Turn this on to put the last one first.',
    },
    col1: { type: 'slot', label: 'Column 1', allow: ANY },
    col1Opts: columnOptionsField(1),
    col2: { type: 'slot', label: 'Column 2', allow: ANY },
    col2Opts: columnOptionsField(2),
    col3: { type: 'slot', label: 'Column 3', allow: ANY },
    col3Opts: columnOptionsField(3),
    col4: { type: 'slot', label: 'Column 4', allow: ANY },
    col4Opts: columnOptionsField(4),
  },

  defaultProps: {
    split: '1-1',
    gap: '7',
    valign: 'center',
    reverseOnMobile: false,
    col1: [],
    col2: [],
    col3: [],
    col4: [],
    col1Opts: { ...COLUMN_DEFAULTS },
    col2Opts: { ...COLUMN_DEFAULTS },
    col3Opts: { ...COLUMN_DEFAULTS },
    col4Opts: { ...COLUMN_DEFAULTS },
  },

  /** Hide the slots this split does not use, so the panel only shows real columns. */
  fieldVisibility(props) {
    const n = columnCount(props.split);
    return {
      col1: n >= 1,
      col1Opts: n >= 1,
      col2: n >= 2,
      col2Opts: n >= 2,
      col3: n >= 3,
      col3Opts: n >= 3,
      col4: n >= 4,
      col4Opts: n >= 4,
    };
  },

  toIR(props): IRNode {
    const n = columnCount(props.split);
    const rowValign = readVAlign(props.valign, 'start');

    const columns: IRNode[] = [];
    for (let i = 1; i <= n; i++) {
      const opts = readColumnOptions(props[`col${i}Opts` as keyof ColumnsProps]);
      columns.push(
        slotContainer(`col${i}`, 'Block', 'div', columnSlot(opts, rowValign), `Column ${i}`),
      );
    }

    return el(
      'Grid',
      'div',
      boxCls(
        'is-grid',
        `is-split-${props.split}`,
        `is-gap-${props.gap}`,
        // Deliberately NOT `is-align-stretch`: `is-grid` already sets `align-items: stretch`,
        // so the class added nothing but a second selector — and the editor has a card-
        // equalising rule keyed on `.is-grid.is-align-stretch` that force-grows grid children.
        // On a Columns block that reached the blocks *inside* each column and spread them out.
        props.reverseOnMobile && 'is-reverse-sm',
      ),
      columns,
      { displayName: 'Columns' },
    );
  },

  signals(): BlockSignal[] {
    return [];
  },

  validate(props): Issue[] {
    const n = columnCount(props.split);
    const empty: number[] = [];
    for (let i = 1; i <= n; i++) {
      const col = props[`col${i}` as keyof ColumnsProps];
      if (!Array.isArray(col) || col.length === 0) empty.push(i);
    }
    if (empty.length === n) {
      return [
        {
          id: 'columns-empty',
          severity: 'info',
          message: 'No content in any column yet.',
          location: { fieldPath: 'col1' },
        },
      ];
    }
    if (empty.length) {
      return [
        {
          id: 'columns-partly-empty',
          severity: 'warn',
          message: `Column ${empty.join(' and ')} ${empty.length > 1 ? 'are' : 'is'} empty, which leaves a gap in the row.`,
          location: { fieldPath: `col${empty[0]}` },
        },
      ];
    }
    return [];
  },
};
